/**
 * @fileoverview Console logger that intercepts and buffers console output.
 * @module services/console
 * @description
 * Ported 1:1 from the Angular ConsoleLogger service. Exposed as a singleton
 * (`consoleLogger`) preserving the exact public method names. Console
 * interception is performed lazily on first construction, matching the Angular
 * service which intercepted in its constructor.
 *
 * @author Liraz Amir
 */

export interface LogEntry {
  timestamp: Date;
  type: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  args: unknown[];
}

export class ConsoleLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private originalConsole: Record<string, (...args: unknown[]) => void> = {};

  /**
   * List of error messages to filter out (non-critical errors)
   */
  private readonly filteredErrors = [
    'NavigatorLockAcquireTimeoutError',
    'lock:sb-auth-token',
    'lock:sb-',
  ];

  constructor() {
    this.interceptConsole();
  }

  getAllLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsAsText(): string {
    return this.logs
      .map((log) => {
        const timestamp = log.timestamp.toLocaleTimeString();
        const type = log.type.toUpperCase();
        return `[${timestamp}] ${type}: ${log.message}`;
      })
      .join('\n');
  }

  clearLogs(): void {
    this.logs = [];
  }

  copyLogsToClipboard(): Promise<boolean> {
    const logsText = this.getLogsAsText();

    if (!logsText) {
      return Promise.resolve(false);
    }

    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard
        .writeText(logsText)
        .then(() => true)
        .catch(() => false);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = logsText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return Promise.resolve(true);
      } catch {
        document.body.removeChild(textArea);
        return Promise.resolve(false);
      }
    }
  }

  destroy(): void {
    // Restore original console methods
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
  }

  private interceptConsole(): void {
    // Store original console methods
    this.originalConsole.log = console.log;
    this.originalConsole.warn = console.warn;
    this.originalConsole.error = console.error;
    this.originalConsole.info = console.info;
    this.originalConsole.debug = console.debug;

    // Override console methods
    console.log = (...args: unknown[]) => {
      this.addLog('log', args);
      this.originalConsole.log.apply(console, args);
    };

    console.warn = (...args: unknown[]) => {
      this.addLog('warn', args);
      this.originalConsole.warn.apply(console, args);
    };

    console.error = (...args: unknown[]) => {
      // Filter out non-critical errors
      if (this.shouldFilterError(args)) {
        // Downgrade to warning instead of error
        this.addLog('warn', ['[Filtered non-critical error]', ...args]);
        this.originalConsole.warn.apply(console, ['[Non-critical]', ...args]);
        return;
      }

      this.addLog('error', args);
      this.originalConsole.error.apply(console, args);
    };

    console.info = (...args: unknown[]) => {
      this.addLog('info', args);
      this.originalConsole.info.apply(console, args);
    };

    console.debug = (...args: unknown[]) => {
      this.addLog('debug', args);
      this.originalConsole.debug.apply(console, args);
    };
  }

  private addLog(type: LogEntry['type'], args: unknown[]): void {
    const entry: LogEntry = {
      timestamp: new Date(),
      type,
      message: this.formatArgs(args),
      args,
    };

    this.logs.push(entry);

    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  private formatArgs(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');
  }

  /**
   * Check if an error should be filtered out
   */
  private shouldFilterError(args: unknown[]): boolean {
    // Check each argument
    for (const arg of args) {
      // Handle Error objects specially
      if (arg instanceof Error) {
        const errorString = `${arg.name} ${arg.message}`.toLowerCase();
        if (this.filteredErrors.some((filter) => errorString.includes(filter.toLowerCase()))) {
          return true;
        }
      }

      // Handle string arguments
      if (typeof arg === 'string') {
        const argLower = arg.toLowerCase();
        if (this.filteredErrors.some((filter) => argLower.includes(filter.toLowerCase()))) {
          return true;
        }
      }

      // Handle object arguments (convert to string)
      if (typeof arg === 'object' && arg !== null) {
        const argString = String(arg).toLowerCase();
        if (this.filteredErrors.some((filter) => argString.includes(filter.toLowerCase()))) {
          return true;
        }
      }
    }

    return false;
  }
}

export const consoleLogger = new ConsoleLogger();
